import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    ConversionDal,
    PluginContext,
    PluginStageHandler
} from '../types'
import {
    getInnerElementsStylePointer,
    getSourceLayoutPointer,
    getStylePointer,
    getTargetLayoutPointer,
    getTransformationsPointer
} from '../conversionDal/utils/pointer'
import type {
    BuilderStyleItem,
    FontStyleValue,
    LayoutEntryItem,
    StructureEntryItem,
    StyleEntryItem,
    TransformationEntryItem
} from '../conversionDal/types'
import _ from 'lodash'
import type {Pointer, SingleLayoutData} from '@wix/document-services-types'
import {pointerUtils} from '@wix/document-manager-core'
import {type AlgorithmModes, Orientation} from '../enums'

const FONT_SIZE_REGEX = /([\d\.]+)[^\/^\s,]+/

const extractFontSizeFromString = (fontString: string) =>
    parseFloat((FONT_SIZE_REGEX.exec(fontString) ?? ['0', '0'])[1])

export const getFontSize = (fontStyleEntryValue: FontStyleValue): number | undefined => {
    if (typeof fontStyleEntryValue === 'string') {
        return extractFontSizeFromString(fontStyleEntryValue)
    }
    return undefined
}

export const setFontSize = (fontStyleEntryItem: BuilderStyleItem<FontStyleValue>, targetFontSize: number): void => {
    fontStyleEntryItem.data = fontStyleEntryItem.data.replace(FONT_SIZE_REGEX, `${targetFontSize}spx`)
}
const getDefinedPaths = (obj: Record<string, any>, prefix?: string): Set<string> => {
    const paths = []

    for (const key in obj) {
        if (!obj.hasOwnProperty(key)) {
            continue
        }

        const value = obj[key]
        const path = prefix ? `${prefix}/${key}` : key

        if (Array.isArray(value)) {
            paths.push(path)
        } else if (typeof value === 'object' && value !== null) {
            paths.push(...getDefinedPaths(value, path))
        } else {
            paths.push(path)
        }
    }

    return new Set(paths)
}

export const updateTargetLayout = (
    {conversionDal, componentPointer}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>,
    layout: Partial<SingleLayoutData>
) => {
    conversionDal.modify(getTargetLayoutPointer(componentPointer), (value: LayoutEntryItem) => {
        const definedPaths = getDefinedPaths(layout)

        return {
            ...value,
            ...{
                variableConnections: value?.variableConnections?.filter(({path}) => !definedPaths.has(path))
            },
            ...layout
        }
    })
}

export const updateTransformations = (
    {conversionDal, componentPointer}: ComponentStageData,
    transformation: Partial<TransformationEntryItem>
) => {
    conversionDal.modify(getTransformationsPointer(componentPointer), (value: TransformationEntryItem) => ({
        ...value,
        ...transformation
    }))
}

export const updateStyle = ({conversionDal, componentPointer}: ComponentStageData, style: Partial<StyleEntryItem>) => {
    conversionDal.modify(getStylePointer(componentPointer), (value: StyleEntryItem) => ({...value, ...style}))
}

export const updateInnerElementsStyle = (
    {conversionDal, componentPointer}: ComponentStageData,
    style: Partial<StyleEntryItem>
) => {
    conversionDal.modify(getInnerElementsStylePointer(componentPointer), (value: StyleEntryItem) => ({
        ...value,
        ...style
    }))
}

export const removeStyleProp = ({conversionDal, componentPointer}: ComponentStageData, prop: keyof StyleEntryItem) => {
    const stylePointer = getStylePointer(componentPointer)
    const currentStyle = conversionDal.get(stylePointer)
    const currentStyleWithoutFontSize = _.omit(currentStyle, [prop])
    conversionDal.set(getStylePointer(componentPointer), currentStyleWithoutFontSize)
}

export const createAdaptiveHeuristic =
    (
        fallbackCallback: PluginStageHandler<ComponentStageData>,
        adaptiveCallback?: PluginStageHandler<ComponentStageData>
    ): PluginStageHandler<ComponentStageData> =>
    (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
        const {initialSnapshot, componentPointer} = componentStageData

        if (!initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))) {
            return fallbackCallback(componentStageData, contextHelper)
        }

        if (adaptiveCallback) {
            return adaptiveCallback(componentStageData, contextHelper)
        }
    }

export const getParent = (conversionDal: ConversionDal, componentPointer: Pointer): Pointer | undefined => {
    const structure: StructureEntryItem = conversionDal.get(componentPointer)
    const parentPointer = pointerUtils.getPointerFromPointer(structure.parent, componentPointer)

    if (!conversionDal.get(parentPointer)) {
        return
    }

    return parentPointer
}

export const isPinned = (componentStagedData: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>) => {
    const {conversionDal, componentPointer} = componentStagedData
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    return layout?.itemLayout?.type === 'FixedItemLayout' || layout?.itemLayout?.pinnedToContainer === true
}

export const isPinnedToContainer = (componentStagedData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStagedData
    return !!getParent(conversionDal, componentPointer)
}

export const predicatePinnedHeuristic = (
    componentStagedData: ComponentStageData,
    horizontalOrVertical: Orientation,
    min: number,
    max: number | undefined,
    predicate: (justifySelf: 'start' | 'center' | 'end', alignSelf: 'start' | 'center' | 'end') => boolean
) => {
    const {conversionDal, componentPointer} = componentStagedData
    if (!isPinned(componentStagedData)) return false
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout} = layout
    const {justifySelf, alignSelf, margins} = itemLayout
    const {left, right, bottom, top} = margins
    const marginVal =
        horizontalOrVertical === Orientation.Horizontal ? left?.value ?? right?.value : top?.value ?? bottom?.value
    return predicate(justifySelf, alignSelf) && marginVal >= min && (!max || marginVal <= max)
}

export const findHeuristic = (
    heuristics: ComponentStageHeuristicDefinition[],
    componentStageData: ComponentStageData
): ComponentStageHeuristicDefinition => heuristics.find(({predicate}) => predicate(componentStageData))!

export const generatePluginHandlerPerMode =
    (
        modeToRunnerMap: Record<AlgorithmModes, PluginStageHandler<ComponentStageData>>
    ): PluginStageHandler<ComponentStageData> =>
    (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
        modeToRunnerMap[componentStageData.config.mode]?.(componentStageData, contextHelper)
